/**
 * Utility functions for pseudo box components
 */

/**
 * Maximum length for pseudo/username display before truncation.
 * Based on viewBox width (307px), font size (42px), and padding (20px each side).
 * With ellipsis "..." (3 chars), total display is MAX_LENGTH + 3.
 * Reduced to 9 to fit wider characters (like 'w', 'm') safely within bounds.
 */
export const PSEUDO_MAX_LENGTH = 9;

/**
 * Configuration for pseudo box SVG layout and styling
 */
export const PSEUDO_BOX_CONFIG = {
  /** Default SVG viewBox dimensions */
  DEFAULT_VIEWBOX: '0 0 307 102',

  /** Text positioning */
  TEXT: {
    Y_POSITION: 50,
    FONT_SIZE: 42,
    LEFT_X_POSITION: 20, // Left padding from edge
    RIGHT_X_POSITION: 287 // Right edge minus padding (307 - 20)
  },

  /** Truncation settings */
  TRUNCATION: {
    MAX_LENGTH: PSEUDO_MAX_LENGTH,
    ELLIPSIS: '...',
    FALLBACK_TEXT: 'Player' // Default text when pseudo is empty
  }
} as const;

/**
 * Truncates a pseudo/username to fit within the pseudo box display.
 * Adds ellipsis (...) if truncation occurs.
 *
 * @param pseudo - The player's username to display
 * @param maxLength - Maximum characters before truncation (default: 9)
 * @returns Formatted pseudo text with ellipsis if needed, or empty string if no player
 *
 * @example
 * formatPseudoForDisplay('Neo') // Returns: 'Neo'
 * formatPseudoForDisplay('VeryLongUsername') // Returns: 'VeryLongU...'
 * formatPseudoForDisplay('') // Returns: '' (empty - no player in slot)
 */
export function formatPseudoForDisplay(
  pseudo: string,
  maxLength: number = PSEUDO_MAX_LENGTH
): string {
  // Handle empty or whitespace-only pseudo - return empty (no player in this slot)
  if (!pseudo || pseudo.trim().length === 0) {
    return '';
  }

  // Truncate with ellipsis if exceeds max length
  return pseudo.length > maxLength
    ? `${pseudo.slice(0, maxLength)}${PSEUDO_BOX_CONFIG.TRUNCATION.ELLIPSIS}`
    : pseudo;
}
